/**
 * HorsIndex — Ressources
 *
 * Source unique des contenus éditoriaux. Pour ajouter une nouvelle ressource,
 * il suffit d'ajouter un objet à la fin du tableau `RESSOURCES`. La page index
 * et les pages individuelles se mettent à jour automatiquement. Penser à
 * ajouter aussi l'URL au sitemap (`/app/frontend/public/sitemap.xml`).
 *
 * Schéma d'un article :
 *   slug      — identifiant URL (kebab-case)
 *   categorie — étiquette éditoriale (eyebrow)
 *   titre     — H1 de la page article + titre de la carte
 *   resume    — texte de la carte index ET lead/intro de l'article
 *   body      — tableau de blocs structurés. Chaque bloc a :
 *                 { type: "h2" | "p" | "quote" | "note", text: string }
 */

const ARTICLES = [
  {
    slug: "panorama-acteurs-donnees-entreprises-france",
    categorie: "Panorama",
    theme: "Écosystème français",
    seoTitle: "Qui diffuse les données d'entreprise en France ?",
    seoDescription:
      "INPI, INSEE, BODACC, Pappers, Altares, Google : le rôle de chaque acteur français dans la circulation et la rediffusion des données d'entreprise.",
    titre: "Qui diffuse les données d'entreprise en France ? Panorama des acteurs et des circuits",
    resume:
      "INPI, INSEE, BODACC, portails publics, rediffuseurs privés, fournisseurs de données professionnelles, moteurs de recherche : un panorama fonctionnel des acteurs français et de leur rôle respectif dans la circulation des données d'entreprise.",
    updated: "juillet 2026",
    body: [
      {
        type: "p",
        text:
          "La circulation d'une donnée d'entreprise ne dépend pas d'un acteur unique. Entre la formalité déclarée par une société et la fiche nominative qu'une recherche fait apparaître en quelques secondes, la donnée traverse une succession d'opérations et d'intermédiaires ayant chacun un rôle différent : production, diffusion officielle, réutilisation privée, enrichissement, exposition publique, indexation, accès professionnel réservé.",
      },
      {
        type: "p",
        text:
          "Ce panorama n'est ni un annuaire exhaustif, ni un classement, ni une liste d'acteurs présentés comme fautifs. La mention d'une plateforme ne signifie pas que ses pratiques seraient illicites. L'objectif est fonctionnel : comprendre la place de chaque catégorie dans la chaîne de circulation, afin de savoir à qui s'adresse chaque type de demande.",
      },
      {
        type: "h2",
        text: "Le circuit d'une donnée d'entreprise",
      },
      {
        type: "p",
        text:
          "Avant de détailler les catégories d'acteurs, il est utile de visualiser le parcours type d'une donnée, de sa production à sa réutilisation :",
      },
      {
        type: "frise",
        title: "Circuit type",
        items: [
          "Registre ou formalité officielle : la donnée est produite dans un cadre légal, avec une finalité déterminée.",
          "Portail public ou fichier ouvert : la donnée est diffusée officiellement, consultable ou téléchargeable.",
          "Récupération par API ou téléchargement : des acteurs tiers collectent la donnée, ponctuellement ou en masse.",
          "Agrégation privée : la donnée est croisée avec d'autres sources et structurée en base propre.",
          "Fiche publique enrichie : une page nominative centralise des informations auparavant dispersées.",
          "Indexation par un moteur de recherche : la fiche devient immédiatement retrouvable par une recherche sur un nom.",
          "Copie et réutilisation : le contenu est recopié, cité ou repris par d'autres sites et services.",
        ],
      },
      {
        type: "p",
        text:
          "Chaque étape correspond à une opération distincte, assumée par des acteurs différents, avec des responsabilités propres. C'est pourquoi une même demande n'a pas le même sens (ni le même destinataire) selon le maillon visé.",
      },
      {
        type: "h2",
        text: "Producteurs et détenteurs de données officielles",
      },
      {
        type: "p",
        text:
          "L'INPI tient le Registre national des entreprises (RNE), l'INSEE gère le répertoire Sirene, la DILA publie le BODACC, et les greffes des tribunaux de commerce tiennent les registres relevant de leur compétence. Ces institutions sont les sources primaires : elles produisent et détiennent la donnée dans le cadre de missions légales, à partir des formalités déclarées par les entreprises elles-mêmes.",
      },
      {
        type: "p",
        text:
          "À ce niveau, l'exposition est encadrée par les textes qui organisent chaque registre : la publication poursuit une finalité de transparence économique déterminée. Les leviers d'action y sont spécifiques : rectification d'une information inexacte auprès du registre concerné, et dispositifs de confidentialité prévus par les textes pour certaines informations, comme la possibilité, dans certaines conditions, de limiter la diffusion publique de l'adresse personnelle d'un dirigeant. Demander l'effacement pur et simple d'un registre officiel n'est, en règle générale, pas la démarche adaptée.",
      },
      {
        type: "h2",
        text: "Portails publics de consultation",
      },
      {
        type: "p",
        text:
          "DATA INPI, l'Annuaire des entreprises, le site du BODACC et, avec un statut particulier, Infogreffe permettent la consultation des données officielles. Certains proposent aussi des jeux de données ouverts et des API publiques. Ces portails assurent la diffusion officielle : ils ne créent pas la donnée, mais la rendent accessible, et, pour une partie, techniquement récupérable en masse.",
      },
      {
        type: "p",
        text:
          "C'est ici que se joue le passage de la consultation ponctuelle à la réutilisation à grande échelle. Les leviers d'action se situent auprès de l'organisme diffuseur : dispositifs de retrait ou de limitation de diffusion lorsque les textes les prévoient, signalement d'une information inexacte, demandes relatives aux modalités de mise à disposition.",
      },
      {
        type: "h2",
        text: "Rediffuseurs privés accessibles au grand public",
      },
      {
        type: "p",
        text:
          "Pappers, Société.com, Verif, Infonet, Manageo, Docubiz, les pages entreprises adossées à des médias comme Le Figaro Entreprises, Score3 et d'autres plateformes comparables construisent leur service sur l'agrégation et la mise en forme des données publiques. Ce sont des réutilisateurs : ils ne détiennent pas la donnée à la source, mais deviennent responsables de leur propre traitement lorsqu'ils la rediffusent.",
      },
      {
        type: "p",
        text:
          "C'est dans cette catégorie que se forme l'essentiel de la surface publique d'exposition : fiches nominatives librement accessibles, indexées par les moteurs de recherche, parfois assorties de documents téléchargeables, de représentations cartographiques ou de mises en relation entre personnes, sociétés et participations. Les leviers d'action correspondants sont ceux du cadre européen de protection des données, exercés directement auprès de la plateforme : demande d'information, rectification, masquage, opposition, retrait ou désindexation selon les cas.",
      },
      {
        type: "p",
        text:
          "Ces plateformes ne sont pas équivalentes entre elles. Pour les comparer sans les caricaturer, une grille descriptive commune peut être appliquée à chacune, lorsque les informations sont vérifiables à la date de consultation :",
      },
      {
        type: "list",
        items: [
          "pages accessibles sans compte ;",
          "recherche possible par nom de personne ;",
          "indexation des fiches dans les moteurs de recherche ;",
          "documents téléchargeables ;",
          "cartographie ou représentation des liens ;",
          "existence d'un espace gratuit ;",
          "existence d'un espace authentifié ou payant ;",
          "procédure visible de rectification, d'opposition, de masquage ou de retrait.",
        ],
      },
      {
        type: "p",
        text:
          "L'application de cette grille varie sensiblement d'un acteur à l'autre et évolue dans le temps : présence ou non d'une recherche nominative, ampleur des documents proposés, visibilité de la procédure de retrait. C'est précisément cette variation qui justifie un examen acteur par acteur avant toute démarche, plutôt qu'une approche indifférenciée.",
      },
      {
        type: "h2",
        text: "Fournisseurs de données professionnelles",
      },
      {
        type: "p",
        text:
          "Des acteurs comme Altares ou Ellisphere, ainsi que les fournisseurs de scoring, de solvabilité, de conformité, de prospection commerciale et d'API de données, servent principalement des clients professionnels : banques, assureurs, directions achats, services conformité. L'accès y est le plus souvent contractuel, authentifié et payant.",
      },
      {
        type: "p",
        text:
          "Leur surface publique d'exposition est généralement plus réduite que celle des rediffuseurs grand public, mais leurs traitements peuvent être étendus, et un accès réservé n'est pas automatiquement conforme. Les leviers pertinents relèvent surtout du droit d'accès, du droit d'information sur la finalité, la base juridique et les destinataires, et, selon les situations, de l'opposition ou de la rectification.",
      },
      {
        type: "h2",
        text: "Moteurs de recherche et intermédiaires d'accès",
      },
      {
        type: "p",
        text:
          "Google, Bing et les autres moteurs ne produisent ni ne rediffusent la donnée au sens strict : ils la rendent trouvable. L'indexation nominative transforme une fiche existante en résultat immédiat pour quiconque recherche un nom. Les mécanismes de cache et les services d'archivage prolongent par ailleurs la visibilité d'un contenu, parfois après sa modification ou son retrait sur le site d'origine.",
      },
      {
        type: "p",
        text:
          "Les leviers correspondants sont spécifiques : demandes de désindexation adressées au moteur, demandes de mise à jour du cache après modification d'une page source, démarches propres aux services d'archivage. Une désindexation ne supprime pas la page d'origine : elle réduit sa découvrabilité, ce qui, dans de nombreuses situations, constitue déjà l'essentiel de l'enjeu.",
      },
      {
        type: "h2",
        text: "Acteurs du contrôle et des recours",
      },
      {
        type: "p",
        text:
          "La chaîne se referme sur les acteurs vers lesquels les demandes convergent : le responsable de traitement de chaque service et, lorsqu'il existe, son délégué à la protection des données (DPO) ; la CNIL, qui peut être saisie d'une réclamation après les démarches préalables ; les moteurs de recherche pour les demandes de désindexation ; et, dans les situations qui le justifient, les juridictions compétentes.",
      },
      {
        type: "p",
        text:
          "L'ordre habituel des démarches reflète la structure du circuit : s'adresser d'abord à l'acteur qui maîtrise réellement l'opération contestée, documenter les échanges, puis élargir si nécessaire. Une demande adressée au mauvais maillon, par exemple demander à un moteur de recherche de corriger une donnée de registre, reste généralement sans effet.",
      },
      {
        type: "h2",
        text: "Ce que ce panorama permet de comprendre",
      },
      {
        type: "p",
        text:
          "Aucun de ces acteurs ne détermine à lui seul l'exposition d'une personne. La donnée est produite par les registres, diffusée par les portails publics, récupérée et enrichie par les réutilisateurs, exposée sur des fiches publiques, rendue immédiatement trouvable par les moteurs, puis copiée et reprise. Chaque maillon a son rôle, sa responsabilité propre et ses leviers spécifiques. Distinguer la source primaire du réutilisateur, la diffusion officielle de l'exposition publique générale, et l'accès professionnel réservé de la fiche indexée : c'est la condition d'une démarche ciblée et efficace.",
      },
      {
        type: "links",
        title: "Pour aller plus loin",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/rediffuseurs-donnees-entreprises", label: "Comment fonctionnent les rediffuseurs de données d'entreprises ?" },
          { to: "/comprendre/pappers-origami-donnees-publiques", label: "Pappers, Origami et le faux argument du « c'est public »" },
          { to: "/comprendre/publicite-legale-ou-exposition-permanente", label: "Publicité légale ou exposition permanente ?" },
          { to: "/comprendre/open-data-protection-personnes-limite", label: "Open data et protection des personnes : où placer la limite ?" },
          { to: "/comprendre/pourquoi-votre-adresse-personnelle-sur-internet", label: "Pourquoi votre adresse personnelle se retrouve sur Internet" },
          { to: "/comprendre/effacement-donnees-personnelles", label: "Effacement, masquage, désindexation : que peut-on réellement demander ?" },
        ],
      },
      {
        type: "note",
        text:
          "Panorama mis à jour en juillet 2026. Les caractéristiques des acteurs cités évoluent et doivent être vérifiées à la date de consultation. La mention d'un acteur ne signifie pas que ses pratiques seraient illicites ; ce texte décrit des rôles fonctionnels, sans porter d'appréciation juridique sur des situations particulières.",
      },
    ],
  },

  {
    slug: "donnee-publique-exposition-publique",
    categorie: "Référence",
    theme: "Cadre général",
    seoTitle: "Donnée publique et exposition publique",
    seoDescription:
      "Une donnée peut être publique à la source sans devenir visible partout, tout le temps, par tout le monde. La distinction qui structure l'action de HorsIndex.",
    titre: "Donnée publique et exposition publique : une distinction essentielle",
    resume:
      "Une donnée peut être publique à la source sans devoir devenir visible partout, tout le temps et par tout le monde. Ce texte pose la distinction qui structure l'action de HorsIndex et relie les autres contenus du site.",
    body: [
      {
        type: "p",
        text:
          "Une grande partie des difficultés rencontrées par les personnes exposées en ligne tient à une confusion : parce qu'une information provient d'une source publique, sa rediffusion générale paraîtrait aller de soi. Or il existe une différence considérable entre la détention d'une donnée dans une base interne, son accès dans un service professionnel réservé, sa diffusion sur une page librement accessible, son indexation nominative par les moteurs de recherche, sa mise à disposition dans des documents téléchargeables et sa présentation sous forme de cartographie. C'est cette gradation, et non une opposition simpliste entre gratuit et payant, qui permet de comprendre ce que HorsIndex traite en priorité : la surface publique d'exposition numérique.",
      },
      {
        type: "h2",
        text: "Une donnée accessible n'est pas nécessairement une donnée neutre",
      },
      {
        type: "p",
        text:
          "Le fait qu'une information ait été publiée dans un cadre légal ne dit rien de l'effet produit par sa circulation ultérieure. Une mention dans un registre consulté par quelques professionnels et une fiche nominative apparaissant en première page d'une recherche sur un nom ne représentent pas la même réalité pour la personne concernée. La donnée est identique ; l'exposition ne l'est pas. Évaluer une situation suppose donc de regarder au-delà de l'origine de l'information : qui peut y accéder, avec quelle facilité, sous quelle forme et avec quelles possibilités de réutilisation.",
      },
      {
        type: "h2",
        text: "Diffusion initiale et rediffusion privée ne sont pas la même opération",
      },
      {
        type: "p",
        text:
          "La publication initiale d'une donnée dans un registre officiel poursuit une finalité déterminée, encadrée par les textes qui l'organisent. Lorsqu'un acteur privé reprend cette donnée pour la diffuser à son tour, il réalise un traitement distinct : il doit pouvoir justifier de sa propre finalité et respecter les règles applicables, notamment celles issues du cadre européen de protection des données. La publicité de la source ne rend pas automatiquement toute republication nécessaire ou proportionnée. Cette distinction ne signifie pas que toute rediffusion serait illicite : elle signifie qu'elle s'apprécie, et que la question mérite d'être posée au cas par cas.",
      },
      {
        type: "h2",
        text: "L'agrégation change la portée de l'information",
      },
      {
        type: "p",
        text:
          "Une donnée isolée dans une source, une autre ailleurs, une troisième dans un document ancien : dispersées, ces informations restent difficilement exploitables. Regroupées sur une fiche unique (nom, mandats, sociétés, adresses, participations, historique), elles produisent un effet nouveau. Le passage d'une donnée dispersée à une fiche centralisée et immédiatement accessible constitue un changement d'échelle de l'exposition. Ce n'est pas la donnée qui a changé ; c'est la facilité de croisement et la mise en relation des informations.",
      },
      {
        type: "h2",
        text: "L'indexation rend une information immédiatement retrouvable",
      },
      {
        type: "p",
        text:
          "L'indexation nominative par les moteurs de recherche transforme une information disponible en une information trouvée. Sans indexation, accéder à une donnée suppose de connaître le site qui la publie et d'y effectuer une recherche. Avec l'indexation, il suffit de saisir un nom. Cette différence de friction est déterminante : elle sépare une consultation motivée d'une découverte immédiate, ouverte à toute personne, sans compte, sans abonnement et sans motif particulier.",
      },
      {
        type: "h2",
        text: "Les documents téléchargeables facilitent la copie et la circulation",
      },
      {
        type: "p",
        text:
          "Un document publiquement téléchargeable (extrait, acte, statuts, liste) présente une caractéristique propre : une fois copié, il échappe à la source qui le diffusait. Il peut être conservé, transmis, republié, croisé avec d'autres éléments, sans que la personne concernée puisse en suivre la circulation. La question de la mise à disposition générale de tels fichiers se distingue donc de celle de la simple consultation d'une page.",
      },
      {
        type: "h2",
        text: "La cartographie ajoute une dimension de localisation et de mise en relation",
      },
      {
        type: "p",
        text:
          "Les représentations cartographiques, comme les cartes d'adresses, les graphes de liens capitalistiques ou les schémas reliant personnes, entreprises et participations, ne se contentent pas d'afficher des données : elles les spatialisent et les relient. Une adresse figurant dans un acte devient un point sur une carte ; une succession de mandats devient un réseau visualisable. Cette mise en forme facilite des usages que la publication initiale n'avait pas envisagés, et peut toucher directement la sphère personnelle ou patrimoniale.",
      },
      {
        type: "h2",
        text: "Pourquoi HorsIndex agit prioritairement sur la partie publique",
      },
      {
        type: "p",
        text:
          "HorsIndex concentre son action prioritaire sur les formes d'exposition les plus ouvertes, intrusives et facilement exploitables : pages accessibles sans authentification, résultats indexés, documents téléchargeables, cartographies et agrégations consultables par tous. C'est là que le déséquilibre entre la finalité initiale d'une publication et son exploitation effective est le plus manifeste, et c'est là que les démarches produisent les effets les plus concrets pour la personne concernée.",
      },
      {
        type: "p",
        text:
          "Cette priorité n'implique aucun blanc-seing pour les espaces réservés. Un espace payant ou authentifié reste soumis au droit applicable, et une analyse complémentaire de ces espaces peut être menée lorsque la situation le nécessite. Il serait tout aussi inexact de dire que les plateformes peuvent « faire ce qu'elles veulent » dans leurs espaces abonnés que de prétendre qu'aucune donnée publique ne pourrait jamais être rediffusée.",
      },
      {
        type: "h2",
        text: "Ce que HorsIndex demande réellement aux plateformes",
      },
      {
        type: "p",
        text:
          "Une demande efficace commence par une délimitation claire de son objet. Plutôt que de réclamer indistinctement « la suppression de tout », la démarche précise ce qui est visé et ce qui ne l'est pas. Une formulation de délimitation peut prendre la forme suivante :",
      },
      {
        type: "quote",
        text:
          "Ma demande concerne prioritairement la partie publiquement accessible de votre service : pages consultables sans authentification, indexation par les moteurs de recherche, documents téléchargeables, cartographies et autres formes de présentation permettant une consultation générale de mes données. Elle ne vise pas nécessairement l'effacement de toute donnée de vos systèmes internes ni l'interdiction de tout service professionnel réservé. Elle porte sur la nécessité et la proportionnalité de leur mise à disposition générale, nominative et immédiatement accessible au public.",
      },
      {
        type: "p",
        text:
          "Sur cette base, des demandes distinctes peuvent être formulées selon les cas :",
      },
      {
        type: "list",
        items: [
          "le retrait de la page publique ;",
          "le masquage des données personnelles ;",
          "la désindexation auprès des moteurs de recherche ;",
          "la suppression des documents publiquement téléchargeables ;",
          "la neutralisation d'une cartographie ;",
          "la limitation de l'accès ;",
          "l'opposition à certaines formes de réutilisation ;",
          "une demande d'information sur la finalité, la base juridique et les destinataires du traitement.",
        ],
      },
      {
        type: "p",
        text:
          "Aucune de ces demandes ne constitue un modèle standard utilisable indistinctement : le choix, la combinaison et la motivation dépendent de la source, de la nature des données et de la situation de la personne concernée.",
      },
      {
        type: "h2",
        text: "Ce que HorsIndex ne promet pas",
      },
      {
        type: "p",
        text:
          "HorsIndex ne garantit ni suppression, ni désindexation, ni disparition d'Internet. Le service ne prétend pas qu'une donnée publique ne pourrait jamais être rediffusée, ni que le RGPD interdirait la publication de données d'entreprise. Les résultats dépendent de chaque situation, des réponses des acteurs concernés et, le cas échéant, des autorités saisies. Ce que le service apporte est d'un autre ordre : une lecture organisée de l'exposition, des demandes ciblées et motivées, un suivi documenté des échanges.",
      },
      {
        type: "h2",
        text: "Les démarches possibles selon le niveau d'exposition",
      },
      {
        type: "p",
        text:
          "Toutes les formes d'exposition n'appellent pas les mêmes démarches. Un résultat indexé, une page publique, un document téléchargeable, une cartographie, un espace à compte gratuit, un espace professionnel authentifié ou une base interne ne présentent ni la même accessibilité, ni les mêmes risques de réutilisation, et plusieurs de ces facteurs se cumulent souvent sur une même source.",
      },
      {
        type: "links",
        title: "Pour aller plus loin",
        items: [
          { to: "/comprendre/rediffuseurs-donnees-entreprises", label: "Comprendre comment les données circulent : Comment fonctionnent les rediffuseurs de données d'entreprises ?" },
          { to: "/comprendre/publicite-legale-ou-exposition-permanente", label: "Comprendre le décalage avec la finalité initiale : Publicité légale ou exposition permanente ?" },
          { to: "/comprendre/open-data-protection-personnes-limite", label: "Comprendre pourquoi l'open data ne justifie pas toutes les rediffusions, Open data et protection des personnes" },
          { to: "/comprendre/pourquoi-votre-adresse-personnelle-sur-internet", label: "Comprendre les effets concrets sur les personnes : Pourquoi votre adresse personnelle se retrouve sur Internet" },
          { to: "/comprendre/effacement-donnees-personnelles", label: "Comprendre les leviers mobilisables : Effacement, masquage, désindexation : que peut-on réellement demander ?" },
        ],
      },
      {
        type: "note",
        text:
          "Ce texte présente un cadre d'analyse général. Il ne constitue pas une consultation juridique individualisée et ne préjuge pas de l'issue des démarches, qui dépend de chaque situation.",
      },
    ],
  },

  {
    slug: "pourquoi-votre-adresse-personnelle-sur-internet",
    categorie: "Décryptage",
    theme: "Exposition de l'adresse",
    seoTitle: "Pourquoi votre adresse personnelle est sur Internet",
    seoDescription:
      "Registres, open data, agrégateurs, moteurs de recherche : comment votre adresse personnelle devient visible en ligne, et à quel niveau d'exposition.",
    titre: "Pourquoi votre adresse personnelle se retrouve sur Internet",
    resume:
      "De nombreux dirigeants découvrent que leur adresse privée est visible sur des plateformes qu'ils n'ont jamais utilisées. Comment ces données circulent-elles et pourquoi sont-elles rediffusées ?",
    body: [
      {
        type: "p",
        text:
          "Beaucoup de dirigeants, d'associés ou de représentants de structures patrimoniales découvrent un jour leur adresse personnelle indexée sur un site qu'ils n'ont jamais visité. Cette situation n'a rien d'accidentel : elle résulte d'une chaîne de publications et de rediffusions parfaitement documentée, dont chaque maillon agit légitimement dans son périmètre.",
      },
      {
        type: "h2",
        text: "Le point de départ : les formalités d'immatriculation",
      },
      {
        type: "p",
        text:
          "Lorsqu'une société est créée, modifiée ou dissoute, plusieurs informations sont transmises aux greffes des tribunaux de commerce ou aux registres équivalents. Lorsqu'une personne physique est désignée représentant légal, son adresse personnelle peut figurer parmi les éléments enregistrés. Cette publication poursuit un objectif précis : permettre aux tiers de connaître les personnes qui engagent juridiquement la société.",
      },
      {
        type: "p",
        text:
          "Le cadre est strict, la finalité est claire, l'information est encadrée. À ce stade, l'exposition est limitée et utile : elle répond à une exigence de transparence du commerce.",
      },
      {
        type: "h2",
        text: "La diffusion en open data",
      },
      {
        type: "p",
        text:
          "Depuis plusieurs années, une partie de ces données est mise à disposition sous forme de jeux de données réutilisables. L'objectif est légitime : permettre la recherche, l'analyse économique, la lutte contre la fraude, l'innovation. Mais l'open data n'est pas un cadre fermé : une fois publiée, une donnée peut être téléchargée, recopiée, enrichie et redistribuée par un grand nombre d'acteurs, sans que la personne concernée en soit informée.",
      },
      {
        type: "h2",
        text: "L'agrégation par des plateformes privées",
      },
      {
        type: "p",
        text:
          "Des acteurs privés ont construit leur modèle économique sur l'agrégation, l'enrichissement et la réindexation de ces données. Une fiche dirigeant peut ainsi regrouper, sur une seule page accessible publiquement, le nom, l'adresse personnelle, les mandats sociaux passés et actuels, les sociétés liées, parfois jusqu'au nom du conjoint lorsqu'il apparaît dans des publications légales.",
      },
      {
        type: "p",
        text:
          "Ces plateformes ne créent pas les données : elles les rendent visibles, lisibles et durablement référencées par les moteurs de recherche. Cette mécanique transforme une publication administrative ponctuelle en une exposition permanente, indexée et difficile à maîtriser.",
      },
      {
        type: "h2",
        text: "Le problème n'est pas seulement la donnée, mais son accessibilité",
      },
      {
        type: "p",
        text:
          "Une adresse présente dans un registre ou un document administratif ne produit pas, en soi, une exposition uniforme. Son niveau d'exposition change concrètement lorsqu'elle est :",
      },
      {
        type: "list",
        items: [
          "directement associée au nom d'une personne ;",
          "visible dans les résultats d'un moteur de recherche ;",
          "intégrée dans une fiche synthétique regroupant d'autres informations ;",
          "localisée sur une carte ;",
          "reproduite dans plusieurs documents téléchargeables ;",
          "reliée à des sociétés, des mandats ou des participations.",
        ],
      },
      {
        type: "p",
        text:
          "À donnée constante, chacune de ces modalités augmente l'accessibilité, la facilité de découverte et les possibilités de réutilisation. C'est ce cumul qui constitue le changement d'échelle de la visibilité.",
      },
      {
        type: "h2",
        text: "Pourquoi une adresse visible en ligne pose un problème particulier",
      },
      {
        type: "p",
        text:
          "L'exposition perçue par la personne concernée ne ressemble plus à une publicité légale traditionnelle. Une recherche sur son propre nom peut faire apparaître son adresse privée en première page de résultats, parfois pendant des années, parfois sans corrélation claire avec une activité professionnelle encore en cours.",
      },
      {
        type: "p",
        text:
          "Ce déplacement entre publicité administrative et exposition numérique généralisée n'a pas été pensé par le législateur initial. Il appelle aujourd'hui une analyse au cas par cas, mobilisant la qualification de la source, l'objectif de la rediffusion et les droits offerts par le cadre européen de protection des données.",
      },
      {
        type: "note",
        text:
          "HorsIndex ne promet pas l'effacement de toute information de tous les systèmes. Le service vise d'abord à réduire la diffusion générale et l'accessibilité immédiate des données : qualifier les sources, distinguer les obligations légales des rediffusions privées, et activer les leviers juridiques disponibles lorsqu'ils existent.",
      },
      {
        type: "links",
        title: "Pour situer ce texte",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/rediffuseurs-donnees-entreprises", label: "Comment fonctionnent les rediffuseurs de données d'entreprises ?" },
          { to: "/comprendre/effacement-donnees-personnelles", label: "Effacement, masquage, désindexation : que peut-on réellement demander ?" },
        ],
      },
    ],
  },

  {
    slug: "publicite-legale-ou-exposition-permanente",
    categorie: "Droit & Numérique",
    theme: "Publicité légale",
    titre: "Publicité légale ou exposition permanente ?",
    resume:
      "La publicité légale poursuit un objectif de transparence. Mais sa rediffusion massive par des acteurs privés soulève aujourd'hui des questions nouvelles.",
    body: [
      {
        type: "p",
        text:
          "La publicité légale est une institution ancienne. Annoncer la création, la modification ou la dissolution d'une société, faire connaître l'identité de ses dirigeants, ses statuts, son capital : cette transparence est nécessaire à la sécurité juridique des échanges commerciaux. Elle permet aux contractants, créanciers, salariés et institutions de connaître les acteurs économiques.",
      },
      {
        type: "h2",
        text: "Un cadre conçu avant l'ère numérique",
      },
      {
        type: "p",
        text:
          "Les règles de publicité légale ont été pensées à une époque où l'accès à l'information supposait une démarche active : consulter un journal d'annonces, demander un extrait au greffe, se déplacer ou solliciter un professionnel. Cette friction n'était pas un défaut, c'était une caractéristique du système. La donnée était publique, mais peu visible par défaut.",
      },
      {
        type: "p",
        text:
          "La numérisation a effacé cette friction. Une information autrefois protégée par sa difficulté d'accès est désormais consultable en quelques secondes, indexée par les moteurs de recherche, recopiée par des dizaines de sites et durablement référencée.",
      },
      {
        type: "h2",
        text: "Quand la publicité légale dépasse son objectif initial",
      },
      {
        type: "p",
        text:
          "L'objectif initial de la publicité légale était de rendre une information disponible à ceux qui en avaient un besoin légitime, pas de la diffuser massivement, ni de la lier à d'autres données, ni de la transformer en élément d'identification permanent d'une personne physique.",
      },
      {
        type: "p",
        text:
          "Lorsque l'adresse personnelle d'un ancien dirigeant continue d'apparaître plusieurs années après la fin de ses fonctions, lorsque des sociétés dissoutes de longue date restent rattachées à son nom dans des fiches automatiquement générées, lorsque ces informations sont enrichies de données issues d'autres sources, on ne parle plus seulement de transparence : on parle d'exposition.",
      },
      {
        type: "h2",
        text: "Transparence et vie privée : deux principes légitimes en tension",
      },
      {
        type: "p",
        text:
          "Le droit européen reconnaît la légitimité de la publicité légale. Il reconnaît également le droit à la protection des données personnelles. Ces deux principes ne s'opposent pas frontalement, mais leur articulation dans un environnement numérique massivement indexé suppose une analyse fine au cas par cas.",
      },
      {
        type: "p",
        text:
          "Une rediffusion privée, motivée par un intérêt légitime, ne bénéficie pas automatiquement de la même protection juridique qu'une publication officielle. Le caractère excessif d'une indexation, la qualité de la source qui rediffuse, l'actualité ou l'obsolescence de l'information sont autant d'éléments qui peuvent être discutés.",
      },
      {
        type: "h2",
        text: "Le rôle des juridictions et autorités",
      },
      {
        type: "p",
        text:
          "Les autorités de contrôle nationales et les juridictions européennes ont, à plusieurs reprises, rappelé que la disponibilité initiale d'une donnée dans une source publique n'autorise pas n'importe quel usage. Le contexte de la collecte, la finalité de la rediffusion et la situation de la personne concernée sont des critères mobilisables.",
      },
      {
        type: "p",
        text:
          "Cette construction jurisprudentielle progressive dessine un cadre dans lequel des leviers juridiques peuvent être activés, sans qu'il s'agisse d'une remise en cause de la publicité légale elle-même.",
      },
      {
        type: "note",
        text:
          "HorsIndex distingue systématiquement les registres officiels des rediffusions privées avant toute démarche. Les droits mobilisés diffèrent selon la nature de la source.",
      },
      {
        type: "links",
        title: "Pour situer ce texte",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/open-data-protection-personnes-limite", label: "Open data et protection des personnes : où placer la limite ?" },
          { to: "/comprendre/kbis-nest-pas-votre-cv", label: "Votre Kbis n'est pas votre CV" },
        ],
      },
    ],
  },

  {
    slug: "rediffuseurs-donnees-entreprises",
    categorie: "Comprendre",
    theme: "Circulation des données",
    seoTitle: "Comment fonctionnent les rediffuseurs de données",
    seoDescription:
      "Pappers, Infonet, Societe.com : comment ces plateformes agrègent et rediffusent les données publiques, et pourquoi cela façonne votre exposition en ligne.",
    titre: "Comment fonctionnent les rediffuseurs de données d'entreprises ?",
    resume:
      "Pappers, Infonet, Societe.com et d'autres plateformes s'appuient sur des données publiques. Comprendre leur modèle permet de mieux comprendre les enjeux actuels.",
    body: [
      {
        type: "p",
        text:
          "Plusieurs plateformes spécialisées agrègent des données issues des registres officiels et les présentent sous une forme accessible à un large public. Ces acteurs sont devenus des intermédiaires majeurs entre les administrations publiques et les utilisateurs finaux. Comprendre leur fonctionnement permet d'aborder plus précisément la question de l'exposition numérique.",
      },
      {
        type: "h2",
        text: "Une matière première : les données publiques",
      },
      {
        type: "p",
        text:
          "La plupart de ces plateformes s'appuient sur des sources officielles : registres du commerce et des sociétés, journal officiel, bulletins d'annonces, bases de données mises à disposition au titre de l'open data. Ces données sont publiques et leur usage est, dans son principe, autorisé.",
      },
      {
        type: "p",
        text:
          "La spécificité de ces plateformes ne réside pas dans la possession de données exclusives, mais dans leur capacité à les agréger, les structurer, les enrichir et les rendre exploitables en quelques clics.",
      },
      {
        type: "h2",
        text: "Un modèle économique centré sur l'accessibilité",
      },
      {
        type: "p",
        text:
          "Le modèle économique repose principalement sur la mise à disposition gratuite d'une partie des informations, complétée par des services payants : extraits téléchargeables, alertes automatisées, abonnements professionnels, API pour les acteurs métiers. Les recettes publicitaires et le référencement constituent également une part importante du modèle pour les plateformes les plus visibles.",
      },
      {
        type: "p",
        text:
          "Pour optimiser leur visibilité, ces plateformes produisent un grand volume de pages référencées par les moteurs de recherche : une page par société, une page par dirigeant, des pages liées entre elles, des fiches enrichies de données issues d'autres sources. Cette logique SEO transforme une donnée administrative en page web durablement indexée.",
      },
      {
        type: "h2",
        text: "Pourquoi ces plateformes paraissent omniprésentes",
      },
      {
        type: "p",
        text:
          "Trois caractéristiques expliquent leur omniprésence dans les résultats des moteurs de recherche : un volume très important de pages, une structure technique optimisée pour le référencement, et une ancienneté qui leur confère une autorité aux yeux des algorithmes. Une recherche sur le nom d'un dirigeant fait souvent apparaître plusieurs de ces plateformes en première page.",
      },
      {
        type: "p",
        text:
          "Ce n'est pas un défaut de leur fonctionnement, c'est une conséquence directe de leur modèle. Mais cela explique pourquoi une personne physique ressent une exposition durable, même lorsqu'elle n'a publié aucune information la concernant elle-même.",
      },
      {
        type: "h2",
        text: "Des responsabilités partagées entre sources, plateformes et moteurs",
      },
      {
        type: "p",
        text:
          "Ces plateformes ne créent pas l'information : elles la rediffusent. Mais en la rendant visible, accessible et indexée, elles deviennent des responsables de traitement au sens du droit européen des données. À ce titre, elles sont soumises à des obligations spécifiques : transparence, finalité, durée de conservation, prise en compte des demandes d'exercice des droits.",
      },
      {
        type: "p",
        text:
          "Cette qualification juridique est essentielle : elle ouvre des leviers de qualification, de discussion et, le cas échéant, de retrait ou de désindexation, lorsque les conditions sont réunies et que la situation de la personne concernée le justifie.",
      },
      {
        type: "p",
        text:
          "L'enjeu ne tient d'ailleurs pas seulement à la provenance publique des données, mais aussi à leurs modalités de diffusion. Une même information ne produit pas le même effet selon qu'elle est :",
      },
      {
        type: "list",
        items: [
          "présentée sur une page accessible sans compte ;",
          "indexée nominativement par les moteurs de recherche ;",
          "centralisée sur une fiche regroupant des informations auparavant dispersées ;",
          "proposée dans des documents téléchargeables ;",
          "représentée sous forme de cartographie ;",
          "rapprochée d'autres personnes, sociétés, participations ou adresses.",
        ],
      },
      {
        type: "p",
        text:
          "Ces modalités relèvent de choix de conception et de modèle économique, pas d'une fatalité technique. C'est leur combinaison qui transforme une donnée d'origine administrative en une exposition publique immédiate.",
      },
      {
        type: "note",
        text:
          "L'exercice de ces droits n'est ni automatique ni systématique. Chaque demande suppose une analyse de la source, de la finalité et de la situation de la personne. C'est précisément ce travail que HorsIndex structure.",
      },
      {
        type: "links",
        title: "Pour situer ce texte",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/panorama-acteurs-donnees-entreprises-france", label: "Qui diffuse les données d'entreprise en France ? Panorama des acteurs et des circuits" },
          { to: "/comprendre/pourquoi-votre-adresse-personnelle-sur-internet", label: "Pourquoi votre adresse personnelle se retrouve sur Internet" },
        ],
      },
    ],
  },

  {
    slug: "effacement-donnees-personnelles",
    categorie: "Vie privée",
    theme: "Droits et leviers",
    seoTitle: "Effacement, masquage, désindexation : que demander ?",
    seoDescription:
      "Retrait, masquage, désindexation, opposition, limitation : les demandes réellement possibles selon la source, la finalité et votre situation.",
    titre: "Effacement, masquage, désindexation : que peut-on réellement demander ?",
    resume:
      "Le RGPD ne se limite pas à un droit à l'effacement. Retrait, masquage, désindexation, opposition, limitation : la demande pertinente dépend de la source, de la finalité et de la situation, et aucun effacement général ne peut être promis.",
    body: [
      {
        type: "p",
        text:
          "Le règlement général sur la protection des données ne se limite pas à un droit à l'effacement. Il organise un ensemble cohérent de leviers dont la mobilisation dépend de la situation concrète, de la source des données et de l'objectif poursuivi. Il ne s'agit donc pas de promettre un effacement général, que personne ne peut garantir, mais de choisir la demande la plus adaptée. Comprendre cette articulation est essentiel avant toute démarche.",
      },
      {
        type: "h2",
        text: "L'effacement n'est pas la seule démarche possible",
      },
      {
        type: "p",
        text:
          "Le droit à l'effacement, prévu à l'article 17 du règlement européen, permet de demander la suppression de données personnelles dans certaines conditions précises : lorsque les données ne sont plus nécessaires à la finalité initiale, lorsque le traitement est illicite, lorsque la personne s'oppose légitimement au traitement, ou dans d'autres hypothèses limitativement énumérées.",
      },
      {
        type: "p",
        text:
          "Mais l'effacement n'est pas toujours la solution adaptée. Selon le contexte, d'autres droits peuvent être plus pertinents : rectification d'une information inexacte, opposition à un traitement reposant sur un intérêt légitime, limitation temporaire d'un traitement contesté, ou désindexation d'un contenu sans suppression de la page source.",
      },
      {
        type: "p",
        text:
          "Concrètement, selon les situations, les demandes suivantes peuvent être envisagées :",
      },
      {
        type: "list",
        items: [
          "le retrait d'une page publique ;",
          "le masquage de données personnelles ;",
          "la désindexation dans les moteurs de recherche ;",
          "l'opposition à certaines modalités de diffusion ;",
          "la limitation du traitement ;",
          "le retrait d'un document téléchargeable ;",
          "la neutralisation d'une cartographie ;",
          "la limitation de l'accès public.",
        ],
      },
      {
        type: "h2",
        text: "Les limites prévues par le RGPD lui-même",
      },
      {
        type: "p",
        text:
          "L'article 17 prévoit également des limites à l'effacement. Certaines obligations légales de publication, certaines missions d'intérêt public, certaines finalités d'archivage ou de recherche peuvent justifier le maintien des données. Un registre officiel ne peut pas, en règle générale, être expurgé par simple demande d'effacement adressée à l'administration qui le tient.",
      },
      {
        type: "p",
        text:
          "Ces limites ne sont pas des refus systématiques : ce sont des balises qui imposent une analyse au cas par cas. La frontière entre une donnée légitimement publiée et une rediffusion devenue excessive ne se trace pas a priori.",
      },
      {
        type: "h2",
        text: "Tout dépend de qui diffuse l'information, et à quel titre",
      },
      {
        type: "p",
        text:
          "Avant toute démarche, il est nécessaire de qualifier la source. Une publication officielle relève d'un régime juridique différent d'une rediffusion privée. Un agrégateur commercial n'a pas le même statut qu'un greffe. Une page individuelle indexée par un moteur de recherche peut faire l'objet d'une démarche distincte de celle visant la page source.",
      },
      {
        type: "p",
        text:
          "Cette qualification conditionne le levier mobilisable, l'interlocuteur à saisir, le fondement juridique invoqué et les chances de succès raisonnables de la démarche.",
      },
      {
        type: "h2",
        text: "Chaque situation s'apprécie selon son contexte",
      },
      {
        type: "p",
        text:
          "Une même information peut être traitée différemment selon le contexte : l'ancienneté de la publication, l'actualité de la fonction concernée, la sensibilité de la donnée, l'existence d'un préjudice démontrable, la proportionnalité de l'exposition par rapport à la finalité initiale. La jurisprudence européenne a progressivement précisé ces critères.",
      },
      {
        type: "p",
        text:
          "L'exercice du droit à l'effacement, comme l'exercice du droit d'opposition ou de la désindexation, suppose donc une analyse préalable rigoureuse. Une demande mal qualifiée peut être rejetée sans examen au fond, alors qu'une demande correctement fondée peut aboutir.",
      },
      {
        type: "p",
        text:
          "C'est aussi pourquoi la demande la plus pertinente n'est pas toujours l'effacement total. Dans de nombreuses situations, l'objectif réellement atteignable (et souvent le plus utile) consiste à réduire l'exposition publique : obtenir le retrait ou le masquage d'une page librement accessible, la désindexation nominative, la suppression d'un document téléchargeable ou la neutralisation d'une cartographie, sans nécessairement exiger la disparition de toute donnée de tous les systèmes.",
      },
      {
        type: "encadre",
        title: "Cas particulier : les résultats de recherche Google",
        text:
          "Lorsque l'exposition tient à un résultat associé à une recherche effectuée sur votre nom, une demande de déréférencement peut être adressée à Google. La page reste alors accessible sur son site d'origine, mais cesse, si la demande est acceptée, d'être présentée en réponse à certaines recherches nominatives. Chaque demande est soumise à l'appréciation de Google.",
      },
      {
        type: "note",
        text:
          "HorsIndex ne se substitue pas à un avocat. Le service structure les informations, qualifie les situations et prépare les démarches. Lorsque la situation appelle une appréciation juridique individualisée, le dossier peut être orienté vers un avocat partenaire.",
      },
      {
        type: "links",
        title: "Pour situer ce texte",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/rediffuseurs-donnees-entreprises", label: "Comment fonctionnent les rediffuseurs de données d'entreprises ?" },
          { to: "/comprendre/anciens-mandats-internet", label: "Vous avez quitté l'entreprise. Internet, lui, ne l'a pas oublié." },
        ],
      },
    ],
  },

  {
    slug: "open-data-protection-personnes-limite",
    categorie: "Réflexion",
    theme: "Limites de l'open data",
    seoTitle: "Open data et protection des personnes",
    titre: "Open data et protection des personnes : où placer la limite ?",
    resume:
      "L'ouverture des données publiques répond à des objectifs légitimes. La question devient plus complexe lorsque ces données concernent directement des individus.",
    body: [
      {
        type: "p",
        text:
          "L'ouverture des données publiques est l'une des évolutions majeures des politiques d'information de ces deux dernières décennies. Elle répond à des objectifs reconnus : transparence démocratique, redevabilité des institutions, innovation économique, recherche, lutte contre la fraude. Mais lorsque les données publiques concernent des personnes physiques, la question de la limite mérite d'être posée avec précision.",
      },
      {
        type: "h2",
        text: "L'open data : une intention initiale légitime",
      },
      {
        type: "p",
        text:
          "L'open data n'a pas été conçu pour exposer les individus. Il a été conçu pour rendre l'État plus lisible, faciliter la production de statistiques, permettre la vérification d'informations économiques et sociales, ouvrir des usages nouveaux à des chercheurs, des journalistes, des entreprises ou des citoyens.",
      },
      {
        type: "p",
        text:
          "Dans la plupart des cas, l'open data porte sur des données agrégées, des informations institutionnelles, des indicateurs publics. Ces usages ne posent pas de question particulière en matière de protection des personnes.",
      },
      {
        type: "h2",
        text: "Le glissement vers les données individuelles",
      },
      {
        type: "p",
        text:
          "Une partie des données ouvertes contient toutefois des informations directement rattachées à des personnes physiques : registres de mandats, déclarations d'intérêts, données électorales pour certains scrutins, registres professionnels, bases foncières dans certains pays, registres d'immatriculation des sociétés lorsque le dirigeant est une personne physique.",
      },
      {
        type: "p",
        text:
          "Ce glissement n'est pas anodin. Une donnée ouverte est, par définition, recopiable, réutilisable, exportable et réindexable. Une fois publiée, elle échappe largement au contrôle de l'institution qui l'a mise à disposition.",
      },
      {
        type: "h2",
        text: "Le cadre européen : ouvrir sans exposer",
      },
      {
        type: "p",
        text:
          "Le règlement européen sur la protection des données ne s'oppose pas à l'open data. Il rappelle simplement que toute donnée à caractère personnel, même publique, reste soumise à un cadre de traitement. Cela signifie que la finalité de la rediffusion, sa proportionnalité, la durée de conservation et le contexte d'usage demeurent des paramètres juridiquement structurants.",
      },
      {
        type: "p",
        text:
          "Plusieurs autorités nationales et européennes ont rappelé ce principe : la publication initiale d'une donnée dans un cadre légitime n'autorise pas n'importe quelle réutilisation ultérieure. La proportionnalité, en particulier, est une notion centrale.",
      },
      {
        type: "h2",
        text: "Quatre situations qu'il faut distinguer",
      },
      {
        type: "p",
        text:
          "Pour raisonner précisément, il est utile de distinguer quatre situations trop souvent confondues :",
      },
      {
        type: "list",
        items: [
          "la publication administrative initiale, encadrée par les textes qui l'organisent ;",
          "la réutilisation par un acteur privé, qui constitue un traitement distinct devant justifier sa propre finalité ;",
          "l'exposition publique générale : gratuite, nominative, indexable et consultable sans authentification ;",
          "l'accès plus ciblé, réservé à des utilisateurs authentifiés ou professionnels.",
        ],
      },
      {
        type: "p",
        text:
          "Un espace payant ou authentifié n'est pas pour autant automatiquement conforme : les règles de protection des données s'y appliquent également. HorsIndex concentre prioritairement son action sur l'exposition publique ouverte, la plus immédiate et la plus facilement exploitable, sans exclure l'analyse des espaces réservés lorsque la situation le nécessite.",
      },
      {
        type: "h2",
        text: "Une question collective, pas seulement individuelle",
      },
      {
        type: "p",
        text:
          "Placer la limite, c'est aussi reconnaître qu'il existe des intérêts légitimes des deux côtés. L'ouverture des données publiques sert l'intérêt général. La protection des personnes physiques sert l'intérêt général également. Aucun des deux principes n'est absolu : leur articulation est une construction continue, jurisprudentielle, parfois lente.",
      },
      {
        type: "p",
        text:
          "Cette articulation est aujourd'hui un chantier en cours, à la fois pour les législateurs, les autorités de contrôle, les acteurs privés qui rediffusent les données, les juridictions saisies de litiges, et les personnes concernées qui découvrent leur exposition.",
      },
      {
        type: "h2",
        text: "Ce que HorsIndex observe",
      },
      {
        type: "p",
        text:
          "Dans la pratique, les situations les plus problématiques ne tiennent pas tant à l'open data lui-même qu'à ses prolongements privés : agrégation, enrichissement, indexation extensive, durée de conservation déconnectée de la finalité initiale. C'est sur ces prolongements que des leviers juridiques peuvent souvent être activés.",
      },
      {
        type: "note",
        text:
          "L'objectif n'est pas de refermer l'open data. C'est d'articuler ouverture et protection : selon une logique de proportionnalité, source par source, situation par situation.",
      },
      {
        type: "links",
        title: "Pour situer ce texte",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/publicite-legale-ou-exposition-permanente", label: "Publicité légale ou exposition permanente ?" },
        ],
      },
    ],
  },

  {
    slug: "pourquoi-horsindex-existe",
    categorie: "HorsIndex",
    theme: "Positionnement HorsIndex",
    titre: "Pourquoi HorsIndex existe",
    resume:
      "À l'origine du projet se trouve un constat simple : la transparence économique ne devrait pas conduire à une exposition numérique incontrôlée des personnes.",
    body: [
      {
        type: "p",
        text:
          "HorsIndex n'est pas né d'une idée abstraite. Le projet répond à une situation devenue très concrète pour de nombreux dirigeants, indépendants, mandataires sociaux, associés ou représentants de structures patrimoniales : une exposition numérique extensive, durable et largement imposée.",
      },
      {
        type: "h2",
        text: "Un constat partagé",
      },
      {
        type: "p",
        text:
          "Toute personne ayant exercé une fonction au sein d'une société, d'une SCI ou d'une structure patrimoniale rencontre tôt ou tard la même expérience : une recherche sur son propre nom fait apparaître, sur la première page des résultats, des fiches détaillées que personne n'a jamais consultées de son vivant professionnel. Adresse personnelle, sociétés passées, mandats anciens, parfois informations enrichies depuis d'autres sources : l'agrégation produit un effet qui dépasse largement l'intention initiale des publications légales.",
      },
      {
        type: "p",
        text:
          "Cette exposition n'est pas illégale dans son principe. Elle résulte de mécanismes parfaitement documentés : registres officiels, open data, plateformes d'agrégation, indexation par les moteurs de recherche. Mais sa résultante concrète peut être disproportionnée par rapport à la finalité initiale.",
      },
      {
        type: "h2",
        text: "Une intuition fondatrice",
      },
      {
        type: "p",
        text:
          "L'intuition à l'origine de HorsIndex est simple : la transparence économique poursuit un objectif légitime, mais elle ne devrait pas, par effet collatéral, conduire à une exposition numérique sans cadre clair des personnes physiques qui exercent des responsabilités économiques. La frontière entre publicité légale et exposition permanente mérite d'être tenue.",
      },
      {
        type: "p",
        text:
          "Pour la tenir, il faut une méthode. Identifier les sources, distinguer les obligations légales des rediffusions privées, qualifier les leviers juridiques mobilisables, préparer des démarches documentées. Ce travail ne s'improvise pas et ne se réduit pas à des outils techniques.",
      },
      {
        type: "h2",
        text: "Un positionnement délibérément prudent",
      },
      {
        type: "p",
        text:
          "HorsIndex ne promet pas l'effacement total. Le service ne fait pas disparaître les données publiques. Il ne vise pas l'invisibilité numérique. Ces promesses, lorsqu'elles existent, sont trompeuses et juridiquement fragiles.",
      },
      {
        type: "p",
        text:
          "L'approche est plus prudente : réduire l'exposition évitable, lorsque des leviers juridiques existent, en respectant les obligations légales et les intérêts en présence. La rigueur de cette posture conditionne sa crédibilité.",
      },
      {
        type: "h2",
        text: "Une legaltech qui revendique sa sobriété",
      },
      {
        type: "p",
        text:
          "HorsIndex assume une orientation institutionnelle : un service utile, documenté, juridiquement maîtrisé, conçu pour répondre à un problème devenu massif sans en tirer un effet de mode. La structure porteuse est française, le cadre est européen, la méthode est explicitée publiquement.",
      },
      {
        type: "p",
        text:
          "Ce qui distingue le projet n'est pas une technologie particulière. C'est la décision de traiter le sujet par la qualification juridique avant de mobiliser quelque outil que ce soit.",
      },
      {
        type: "note",
        text:
          "HorsIndex est un service en cours de structuration. La marque verbale HorsIndex a été déposée auprès de l'INPI.",
      },
      {
        type: "links",
        title: "Pour situer ce texte",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/simon-marie", label: "À propos de Simon Marie" },
        ],
      },
    ],
  },
  {
    slug: "supprimer-adresse-annuaire-en-ligne",
    categorie: "Comprendre",
    theme: "Démarches",
    seoTitle: "Adresse personnelle dans un annuaire : comment la faire retirer ?",
    seoDescription:
      "Votre adresse apparaît dans un annuaire en ligne ? Pourquoi elle y figure, ce qui peut être demandé selon la source, et pourquoi supprimer une adresse d'un site et la faire disparaître de Google sont deux démarches distinctes.",
    titre: "Votre adresse apparaît dans un annuaire en ligne : que peut-on faire ?",
    resume:
      "Votre adresse figure sur un annuaire, un site de pages blanches ou une fiche nominative que vous n'avez jamais alimentés ? Cet article explique pourquoi cela arrive, ce qui peut être demandé selon les situations, et pourquoi le retrait à la source et la visibilité sur Google sont deux questions distinctes.",
    updated: "août 2026",
    body: [
      {
        type: "p",
        text:
          "De nombreux sites affichent des noms, adresses postales et numéros de téléphone : héritiers des pages blanches, annuaires inversés, fiches nominatives, agrégateurs qui reconstituent des profils à partir de sources dispersées. Une adresse personnelle peut y figurer sans que vous l'ayez jamais communiquée à ces sites. Dans beaucoup de situations, son retrait peut être demandé, mais la démarche pertinente dépend fortement de la source et du contexte.",
      },
      {
        type: "h2",
        text: "Pourquoi votre adresse peut apparaître sur plusieurs sites",
      },
      {
        type: "p",
        text:
          "Une adresse circule rarement seule. Elle peut provenir d'un ancien abonnement, d'une inscription oubliée, d'une base commerciale revendue, ou, pour les dirigeants et indépendants, d'un registre légal lorsque l'adresse personnelle a servi de siège d'activité. Une fois diffusée, elle est fréquemment reprise par d'autres bases, croisée avec d'autres informations et republiée sur de nouvelles pages. C'est pourquoi il est souvent utile de vérifier si la même information apparaît sur plusieurs sites avant d'engager quoi que ce soit : traiter une page isolément, sans regarder d'où vient la donnée, conduit parfois à la voir réapparaître ailleurs.",
      },
      {
        type: "h2",
        text: "Toutes les sources ne se traitent pas de la même manière",
      },
      {
        type: "p",
        text:
          "Deux pages affichant exactement la même adresse peuvent nécessiter des démarches différentes selon leur source, leur finalité et la manière dont l'information a été obtenue. Une adresse reprise par un annuaire commercial, publiée dans un registre légal ou utilisée comme adresse professionnelle actuelle ne soulève pas les mêmes questions. Certaines informations bénéficient d'un cadre de publication obligatoire ; d'autres sont diffusées sans base sérieuse et peuvent être contestées plus directement. Identifier correctement la nature de chaque source est souvent l'étape qui détermine l'issue de la démarche.",
      },
      {
        type: "h2",
        text: "Peut-on demander le retrait d'une adresse ?",
      },
      {
        type: "p",
        text:
          "Souvent, oui. Le règlement général sur la protection des données (RGPD) prévoit plusieurs droits qui peuvent être mobilisés selon la situation, notamment le droit à l'effacement et le droit d'opposition. Mais la réponse dépend du statut de la donnée, de sa source, de sa finalité, de son actualité, des éventuelles obligations légales de publication et de la situation de la personne concernée. Une demande générique adressée indistinctement à tous les sites a moins de chances d'aboutir qu'une demande adaptée à chaque interlocuteur. En cas de refus ou d'absence de réponse, la CNIL peut par ailleurs être saisie.",
      },
      {
        type: "h2",
        text: "Et si l'adresse apparaît encore sur Google ?",
      },
      {
        type: "p",
        text:
          "C'est une distinction essentielle : obtenir la suppression ou la modification d'une page auprès du site qui la publie, et faire cesser son apparition dans les résultats de recherche, sont deux questions différentes. Une page supprimée peut rester visible quelque temps dans Google ; un site peut aussi refuser de retirer l'information alors qu'une démarche auprès du moteur de recherche reste envisageable. Selon les cas, il faut agir sur la source, sur le moteur, ou sur les deux.",
      },
      {
        type: "h2",
        text: "Pourquoi une information peut-elle réapparaître ?",
      },
      {
        type: "p",
        text:
          "Les annuaires et agrégateurs actualisent régulièrement leurs bases à partir des mêmes sources. Une adresse retirée d'un site peut donc réapparaître, au même endroit ou ailleurs, si une source en amont continue de la diffuser. C'est la raison pour laquelle une analyse globale, qui détermine quelles pages et quelles sources traiter, et dans quel ordre, évite souvent de recommencer indéfiniment des démarches isolées.",
      },
      {
        type: "h2",
        text: "Ce qu'il faut retenir",
      },
      {
        type: "list",
        items: [
          "Une même information peut être reprise sur plusieurs sites.",
          "La démarche dépend de la source et du contexte de publication.",
          "Retrait à la source et visibilité dans Google sont deux questions différentes.",
          "Une analyse globale évite souvent de traiter isolément des pages qui proviennent d'une même source.",
        ],
      },
      {
        type: "note",
        text:
          "Aucune suppression ne peut être garantie dans l'absolu : la réponse dépend du statut de la donnée, de sa source et des obligations légales éventuelles. Certaines informations, notamment issues de publicités légales, obéissent à des règles propres.",
      },
    ],
  },

  {
    slug: "pappers-origami-donnees-publiques",
    categorie: "Décryptage",
    theme: "Agrégation et cartographie",
    seoTitle: "Pappers, Origami et le faux argument du « c'est public »",
    seoDescription:
      "Une donnée peut être publique à la source sans que son agrégation, son indexation nominative et sa mise en relation soient neutres. Analyse du modèle des agrégateurs.",
    titre: "Pappers, Origami et le faux argument du « c'est public »",
    resume:
      "Une information peut être légalement publique sans que son agrégation, son indexation nominative et sa mise en relation soient neutres. Pappers, Origami et les nouveaux agrégateurs rendent ce débat impossible à éviter.",
    updated: "août 2026",
    body: [
      {
        type: "p",
        text:
          "Il suffit aujourd'hui de saisir le nom de certains dirigeants dans Google pour retrouver leurs sociétés actuelles et anciennes, leurs mandats, parfois des éléments d'état civil, des actes juridiques et des liens avec d'autres personnes ou d'autres structures. Rien de tout cela n'a été volé. Chaque élément provient d'un registre, d'une annonce légale ou d'un fichier ouvert. Et pourtant, le résultat obtenu ne ressemble à aucun de ces registres pris isolément.",
      },
      {
        type: "p",
        text:
          "Pappers a largement popularisé ce modèle : rendre la donnée administrative des entreprises immédiatement consultable, gratuite et trouvable depuis un moteur de recherche. Origami Entreprises en propose aujourd'hui une nouvelle déclinaison, en donnant notamment une place importante aux relations entre sociétés et dirigeants et à leur représentation sous forme de cartographies. Il ne s'agit pas de dire que l'un serait pire que l'autre : les deux illustrent une même évolution, dont il faut mesurer les effets.",
      },
      {
        type: "p",
        text:
          "Face à cette évolution, une phrase revient systématiquement : « mais ces informations sont publiques ». Cette phrase est exacte. Elle répond simplement à une autre question que celle qui est posée. Elle renseigne sur l'origine de la donnée. Elle ne dit rien de sa republication, de son agrégation, de son indexation nominative, de sa mise en relation ni de sa visibilité générale.",
      },
      {
        type: "h2",
        text: "Public ne veut pas dire agrégé",
      },
      {
        type: "p",
        text:
          "Cinq situations qui semblent proches recouvrent en réalité des expositions très différentes : une donnée présente dans un registre, plusieurs données dispersées entre différentes sources, une fiche nominative qui les centralise, une page indexée dans Google et une cartographie qui relie l'ensemble. À chaque étape, le contenu factuel change peu. Ce qui change, c'est le contexte et la friction d'accès.",
      },
      {
        type: "p",
        text:
          "Consulter un registre suppose de savoir qu'il existe, de savoir quoi y chercher et de formuler une requête précise. Consulter une fiche nominative agrégée ne suppose rien du tout : un prénom, un nom, un clic. Le droit européen de la protection des données ne raisonne pas autrement lorsqu'il examine un traitement : la CNIL rappelle régulièrement que la réutilisation de données publiquement accessibles reste un traitement à part entière, qui doit avoir sa propre base légale et sa propre proportionnalité. La publicité de la source ne purge pas la question de la rediffusion.",
      },
      {
        type: "h2",
        text: "Un registre n'est pas un moteur de recherche sur les personnes",
      },
      {
        type: "p",
        text:
          "La publicité des informations relatives aux entreprises poursuit une finalité précise et ancienne : sécuriser la vie des affaires. Savoir qu'une société existe, qui peut l'engager, si elle est en procédure collective. Cette transparence est orientée vers l'entreprise, pas vers la biographie de la personne qui la dirige.",
      },
      {
        type: "p",
        text:
          "Vérifier qui représente une société n'est pas exactement la même chose que reconstituer vingt ans de parcours professionnel à partir d'un patronyme. Le premier usage correspond à la finalité du registre. Le second est un usage dérivé, rendu possible par l'agrégation et l'indexation, que les textes fondateurs de la publicité légale n'avaient pas à envisager, tout simplement parce qu'il était matériellement impossible.",
      },
      {
        type: "h2",
        text: "Pappers a rendu la donnée administrative immédiatement accessible",
      },
      {
        type: "p",
        text:
          "Factuellement, Pappers propose des fiches sociétés, des fiches dirigeants, des documents issus des registres et des annonces légales, ainsi que des liens entre sociétés. Ses pages sont librement accessibles et fortement visibles dans les moteurs de recherche : c'est même l'un des ressorts de son succès. Le service a rendu à tout un chacun ce qui demandait auparavant du temps, des connaissances et parfois un paiement.",
      },
      {
        type: "p",
        text:
          "Le sujet n'est pas de mettre en cause l'entreprise, qui exploite des données dont la diffusion est organisée par les textes. Le sujet est l'effet produit par ce modèle de diffusion : lorsque des millions de fiches nominatives deviennent la première réponse d'une recherche sur un nom, l'équilibre entre transparence économique et exposition des personnes se déplace, sans qu'aucune décision publique n'ait formellement arbitré ce déplacement.",
      },
      {
        type: "h2",
        text: "Origami ajoute une logique de réseau",
      },
      {
        type: "p",
        text:
          "Origami Entreprises, plus récent, reprend les mêmes fondations : fiches entreprises et dirigeants construites à partir des sources publiques, documents, annonces, entreprises liées. Il y ajoute, de manière assumée et documentée sur ses pages publiques, une dimension relationnelle : des cartographies qui représentent graphiquement les liens entre sociétés, dirigeants, filiales et participations, une API ouverte, et un serveur MCP qui permet à des outils d'intelligence artificielle d'interroger directement la base.",
      },
      {
        type: "p",
        text:
          "Chacune de ces fonctionnalités, prise isolément, existe ailleurs. Leur combinaison dessine autre chose : une infrastructure où le parcours économique d'une personne n'est plus seulement consultable, mais interrogeable par des machines et représentable en un graphe. La donnée cesse d'être un document que l'on lit pour devenir un réseau que l'on explore.",
      },
      {
        type: "h2",
        text: "Une cartographie ne se contente pas d'afficher une donnée",
      },
      {
        type: "p",
        text:
          "Prenons un exemple abstrait. A et B sont deux sociétés qui figurent séparément dans des registres. Une personne apparaît dans A. La même personne apparaît dans B. Trois informations dispersées, chacune parfaitement licite. Un graphe transforme ces trois informations en une relation immédiatement perceptible : cette personne relie A et B.",
      },
      {
        type: "p",
        text:
          "Ce n'est pas nécessairement une donnée factuelle nouvelle. C'est une nouvelle modalité d'exploitation de données existantes. La représentation relationnelle produit une information plus directement intelligible que la somme de ses sources : elle suggère des proximités, des continuités, des dépendances. Ce que le lecteur d'un registre devait reconstruire par un travail volontaire, la cartographie le donne à voir instantanément, y compris à celui qui ne cherchait rien de précis.",
      },
      {
        type: "h2",
        text: "La face visible est le vrai sujet",
      },
      {
        type: "p",
        text:
          "HorsIndex distingue systématiquement plusieurs niveaux dans ces services : les bases internes, les outils professionnels ou authentifiés, les API, et la partie librement accessible au public. Ces niveaux n'exposent pas les personnes de la même manière et n'appellent pas les mêmes demandes.",
      },
      {
        type: "p",
        text:
          "La priorité concerne la surface publique d'exposition : les pages accessibles sans compte, l'indexation nominative dans les moteurs de recherche, les profils dirigeants, les historiques, les documents téléchargeables, les cartographies et les rapprochements consultables par n'importe qui. C'est cette face visible qui transforme une base de données en exposition permanente.",
      },
      {
        type: "quote",
        text:
          "Le problème n'est pas nécessairement ce que ces bases savent. C'est ce qu'elles montrent à n'importe qui.",
      },
      {
        type: "h2",
        text: "« Je n'ai rien à cacher » ne répond pas à la question",
      },
      {
        type: "p",
        text:
          "L'objection est fréquente et mérite une réponse posée. La protection des données ne repose pas sur l'existence de quelque chose de honteux ou de secret. Elle repose sur l'idée qu'une information, même exacte, ne doit pas circuler sans limite ni contexte.",
      },
      {
        type: "p",
        text:
          "Une information exacte peut devenir problématique selon son ancienneté, son agrégation avec d'autres, et la facilité avec laquelle un tiers quelconque y accède. Une adresse ancienne, une société fermée, un mandat abandonné depuis quinze ans ne relèvent d'aucun secret. Leur juxtaposition permanente en tête d'une recherche nominative pose pourtant une question de proportionnalité que la formule « je n'ai rien à cacher » ne tranche pas : elle l'esquive.",
      },
      {
        type: "h2",
        text: "De la transparence à la reconstruction",
      },
      {
        type: "p",
        text:
          "La trajectoire peut se résumer en une succession simple : registre, puis numérisation, puis moteur de recherche, puis agrégateur, puis profil nominatif, puis cartographie, puis interrogation automatisée par l'intelligence artificielle. Aucune de ces étapes n'est en soi illégale. Chacune a ses justifications, ses usages légitimes et souvent son utilité réelle.",
      },
      {
        type: "p",
        text:
          "Mais leur enchaînement change profondément l'échelle et la facilité d'exploitation. Ce qui demandait une démarche volontaire, du temps et une raison de chercher devient un résultat immédiat, produit sans effort et sans motif. La transparence économique, pensée comme un droit de vérification, devient une capacité de reconstruction : celle du parcours d'une personne, de ses liens et de son histoire, à partir de son seul nom.",
      },
      {
        type: "p",
        text:
          "La bonne question n'est peut-être plus seulement : cette information est-elle publique ? Mais : jusqu'où doit-elle pouvoir être agrégée, reliée, indexée et rendue immédiatement accessible à partir du nom d'une personne ?",
      },
      {
        type: "quote",
        text:
          "Le problème n'est plus seulement que l'information soit publique. Le problème est qu'elle soit devenue instantanément reconstructible.",
      },
      {
        type: "links",
        title: "Pour aller plus loin",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/rediffuseurs-donnees-entreprises", label: "Comment fonctionnent les rediffuseurs de données d'entreprises ?" },
          { to: "/comprendre/panorama-acteurs-donnees-entreprises-france", label: "Qui diffuse les données d'entreprise en France ? Panorama des acteurs" },
          { to: "/comprendre/kbis-nest-pas-votre-cv", label: "Votre Kbis n'est pas votre CV" },
          { to: "/comprendre/effacement-donnees-personnelles", label: "Effacement, masquage, désindexation : que peut-on réellement demander ?" },
        ],
      },
      {
        type: "note",
        text:
          "Texte mis à jour en août 2026. Les fonctionnalités attribuées à Pappers et à Origami Entreprises sont décrites à partir de leurs pages publiquement accessibles à cette date et peuvent évoluer. La mention de ces services ne signifie pas que leurs pratiques seraient illicites. Ce texte présente un cadre général fondé notamment sur le RGPD et les positions publiques de la CNIL ; il ne constitue pas une consultation juridique individualisée.",
      },
    ],
  },

  {
    slug: "kbis-nest-pas-votre-cv",
    categorie: "Réflexion",
    theme: "Identité professionnelle",
    seoTitle: "Votre Kbis n'est pas votre CV",
    seoDescription:
      "Créer une société implique une publicité légale. Cela ne signifie pas que vingt ans de parcours économique doivent devenir une biographie publique accessible en ligne.",
    titre: "Votre Kbis n'est pas votre CV",
    resume:
      "Créer ou diriger une entreprise implique une certaine publicité légale. Cela signifie-t-il pour autant que vingt ans de parcours économique doivent devenir une biographie publique accessible depuis Google ?",
    updated: "août 2026",
    body: [
      {
        type: "p",
        text:
          "Un entrepreneur accepte naturellement que certaines informations sur sa société soient publiques. Sa dénomination, son siège, son représentant légal, ses comptes parfois : c'est le prix, connu et légitime, de la vie des affaires. Ce qu'il n'a en général pas anticipé, c'est que l'ensemble de ses sociétés présentes et passées finirait par constituer, page après page, une sorte de CV parallèle : rédigé par personne, assemblé par des bases de données, et consultable par n'importe qui.",
      },
      {
        type: "p",
        text:
          "Ce glissement mérite d'être regardé en face, sans dramatisation mais sans naïveté. Car il repose sur une série de confusions : entre l'information sur une entreprise et l'information sur une personne, entre un mandat actuel et un historique de mandats, entre la vérification juridique et la curiosité nominative, entre un registre et un moteur de recherche.",
      },
      {
        type: "h2",
        text: "Ce que dit réellement un registre",
      },
      {
        type: "p",
        text:
          "Un extrait d'immatriculation dit des choses précises et limitées : cette société existe, elle est immatriculée sous ce numéro, elle a ce siège, cette personne peut l'engager. C'est un instrument de sécurité juridique, conçu pour répondre à une question ponctuelle au moment où elle se pose : puis-je contracter avec cette entreprise, et avec qui suis-je en train de traiter ?",
      },
      {
        type: "p",
        text:
          "Le registre ne dit pas pourquoi une société a été créée, ce qu'elle a réellement fait, ni dans quelles conditions elle a cessé. Il enregistre des formalités. Il ne raconte pas une histoire, et il n'a jamais eu vocation à en raconter une.",
      },
      {
        type: "h2",
        text: "Quand une fiche entreprise devient une fiche personne",
      },
      {
        type: "p",
        text:
          "L'agrégation inverse la perspective. Tant que les données restent organisées par entreprise, la personne n'apparaît qu'au détour d'une fiche. Dès lors qu'un service construit des pages par dirigeant, la logique bascule : ce n'est plus la société qui est documentée, c'est la personne. Tous ses mandats, toutes ses sociétés, actives, cédées ou radiées, sont rassemblés sous son nom, et cette page nominative est souvent indexée par les moteurs de recherche.",
      },
      {
        type: "p",
        text:
          "Le contenu n'a pas changé : chaque ligne provient d'une formalité légale. Mais l'objet a changé. Une collection de formalités relatives à des entreprises est devenue un profil relatif à une personne. Et un profil, contrairement à un registre, se lit comme un portrait.",
      },
      {
        type: "h2",
        text: "Vingt ans d'activité résumés en quelques lignes",
      },
      {
        type: "p",
        text:
          "Une succession de sociétés ne raconte pas nécessairement correctement une vie professionnelle. Une société fermée peut correspondre à un projet mené à son terme, à une restructuration, à une activité abandonnée faute de temps, à un véhicule créé pour une opération unique, ou à une situation ancienne qui n'a plus aucun rapport avec l'activité actuelle de la personne.",
      },
      {
        type: "p",
        text:
          "Une base de données ne connaît pas ce contexte. Elle enregistre des immatriculations et des radiations, des débuts et des fins de mandat. Elle aligne des dates. Le lecteur, lui, interprète : il voit des créations et des cessations, il en déduit des réussites et des échecs, une stabilité ou une agitation. La base n'affirme rien de tout cela. Elle le laisse conclure.",
      },
      {
        type: "h2",
        text: "Une information exacte peut donner une image fausse",
      },
      {
        type: "p",
        text:
          "Il ne s'agit pas de prétendre que les plateformes falsifient les informations : elles reproduisent, le plus souvent fidèlement, des données issues de sources officielles. Le point est différent. La juxtaposition de faits exacts peut produire une interprétation implicite ou un portrait incomplet.",
      },
      {
        type: "p",
        text:
          "Trois radiations alignées sur une fiche ne disent pas qu'il s'agissait de trois filiales fermées lors d'une réorganisation parfaitement maîtrisée. Un mandat dans une société en liquidation ne dit pas que la personne est arrivée précisément pour gérer cette liquidation. L'exactitude de chaque donnée n'implique pas la justesse de l'image d'ensemble. C'est toute la différence entre une information et un récit.",
      },
      {
        type: "h2",
        text: "Vous choisissez votre CV. Vous ne choisissez pas votre profil d'agrégateur.",
      },
      {
        type: "p",
        text:
          "Un CV est un exercice de présentation maîtrisée : chacun choisit ce qu'il met en avant, ce qu'il contextualise, ce qu'il écarte comme non pertinent. Ce n'est pas une dissimulation, c'est une hiérarchisation. Toute vie professionnelle comporte des projets aboutis, des tentatives interrompues et des choix qui ne se comprennent qu'avec leur contexte.",
      },
      {
        type: "p",
        text:
          "Le profil constitué par un agrégateur obéit à la logique inverse : exhaustivité mécanique, absence de contexte, mise à jour automatique, et présentation identique pour tous. La personne concernée n'en choisit ni le contenu, ni l'ordre, ni la visibilité. Cette différence n'est pas émotionnelle, elle est structurelle : d'un côté une présentation dont la personne est l'auteur, de l'autre une compilation dont elle est seulement l'objet.",
      },
      {
        type: "h2",
        text: "Ce que cela implique, concrètement",
      },
      {
        type: "p",
        text:
          "Prendre ce sujet au sérieux ne conduit pas à revendiquer l'effacement de la publicité légale, qui a ses raisons d'être. Cela conduit à poser une question de proportionnalité : la finalité de vérification qui justifie la publicité des données d'entreprise justifie-t-elle, à elle seule, l'existence de biographies économiques nominatives, exhaustives, permanentes et indexées ?",
      },
      {
        type: "p",
        text:
          "Le cadre européen de protection des données offre des points d'appui pour discuter, au cas par cas, de cette proportionnalité : information, rectification, opposition, limitation de la diffusion publique de certaines données, désindexation nominative selon les situations. Aucun de ces leviers ne garantit un résultat. Tous supposent d'avoir d'abord compris ce que la fiche montre, d'où viennent les données et quel maillon de la chaîne est réellement en cause.",
      },
      {
        type: "quote",
        text:
          "La publicité légale informe sur les entreprises. Elle ne devrait pas automatiquement devenir une biographie professionnelle universelle.",
      },
      {
        type: "links",
        title: "Pour aller plus loin",
        items: [
          { to: "/comprendre/donnee-publique-exposition-publique", label: "Donnée publique et exposition publique : une distinction essentielle" },
          { to: "/comprendre/rediffuseurs-donnees-entreprises", label: "Comment fonctionnent les rediffuseurs de données d'entreprises ?" },
          { to: "/comprendre/pappers-origami-donnees-publiques", label: "Pappers, Origami et le faux argument du « c'est public »" },
          { to: "/comprendre/effacement-donnees-personnelles", label: "Effacement, masquage, désindexation : que peut-on réellement demander ?" },
        ],
      },
      {
        type: "note",
        text:
          "Ce texte présente un cadre général de réflexion. Il ne met en cause la licéité d'aucun service cité ou évoqué et ne constitue pas une consultation juridique individualisée. L'applicabilité des leviers mentionnés dépend de chaque situation.",
      },
    ],
  },

  {
    slug: "anciens-mandats-internet",
    categorie: "Vie numérique",
    theme: "Mémoire numérique",
    seoTitle: "Anciens mandats : pourquoi Internet ne les oublie pas",
    seoDescription:
      "Société fermée, ancien mandat, adresse passée : pourquoi ces informations restent visibles pendant des années, et ce qu'il est réellement possible d'envisager.",
    titre: "Vous avez quitté l'entreprise. Internet, lui, ne l'a pas oublié.",
    resume:
      "Une société fermée, un ancien mandat ou une adresse utilisée autrefois peuvent continuer à apparaître pendant des années. La mémoire des bases de données n'est pas celle des personnes.",
    updated: "août 2026",
    body: [
      {
        type: "p",
        text:
          "Une personne quitte une société en 2012. Elle change de métier, déménage, construit autre chose. Quatorze ans plus tard, une recherche sur son nom peut toujours faire apparaître cette ancienne société parmi les premiers résultats : fiche dirigeant, mandat daté, parfois l'adresse de l'époque. Rien de faux. Rien de récent non plus.",
      },
      {
        type: "p",
        text:
          "Cette situation, extrêmement banale, pose une question simple à formuler et difficile à trancher : pourquoi une information historiquement exacte devrait-elle nécessairement conserver le même niveau de visibilité publique indéfiniment ?",
      },
      {
        type: "h2",
        text: "Internet ne connaît pas naturellement la notion d'ancien",
      },
      {
        type: "p",
        text:
          "La mémoire humaine hiérarchise spontanément : ce qui est récent pèse plus que ce qui est lointain, ce qui est actuel plus que ce qui est révolu. Les bases de données ne fonctionnent pas ainsi. Une ligne créée en 2008 et une ligne créée hier ont exactement le même statut technique : elles existent, elles sont servies, elles sont affichées.",
      },
      {
        type: "p",
        text:
          "Certaines plateformes signalent qu'un mandat est terminé ou qu'une société est radiée. Mais cette mention ne réduit ni l'accessibilité de la page, ni son indexation, ni sa place dans les résultats. L'information est marquée comme ancienne ; elle n'est pas traitée comme telle.",
      },
      {
        type: "h2",
        text: "Historique ne signifie pas nécessairement pertinent",
      },
      {
        type: "p",
        text:
          "Pour raisonner correctement, il faut distinguer des notions que le débat public confond souvent :",
      },
      {
        type: "list",
        items: [
          "la conservation : la donnée existe quelque part, dans un registre ou une base ;",
          "l'archivage : elle est maintenue à des fins de mémoire ou d'obligation légale, selon des modalités propres ;",
          "l'accessibilité : n'importe qui peut la consulter librement, sans motif ni compte ;",
          "l'indexation : une recherche sur un nom la fait remonter immédiatement ;",
          "la pertinence actuelle : elle renseigne encore utilement sur la situation présente de la personne ;",
          "la proportionnalité : son niveau d'exposition est en rapport avec la finalité qui justifie sa publicité.",
        ],
      },
      {
        type: "p",
        text:
          "Une donnée peut légitimement rester conservée et archivée tout en ayant perdu, avec le temps, l'essentiel de sa pertinence pour une consultation générale. Ce sont deux questions différentes, et c'est précisément sur cette distinction que reposent la plupart des leviers existants.",
      },
      {
        type: "h2",
        text: "Google transforme les archives en présent",
      },
      {
        type: "p",
        text:
          "Le moteur de recherche ajoute un effet propre. Une page ancienne, dès lors qu'elle est indexée et qu'elle correspond exactement au nom recherché, peut occuper la première page de résultats des années après les faits. Le moteur ne date pas l'importance : il classe des pages selon leur pertinence par rapport à la requête, et une fiche nominative structurée est, de ce point de vue, un excellent résultat.",
      },
      {
        type: "p",
        text:
          "La conséquence est paradoxale : pour celui qui effectue la recherche, l'information de 2012 se présente exactement comme une information de cette année. Le contexte temporel, évident dans une archive papier, disparaît dans la page de résultats. L'archive est servie au présent.",
      },
      {
        type: "h2",
        text: "Une société disparue peut rester attachée à votre nom",
      },
      {
        type: "p",
        text:
          "Le cas des sociétés radiées est le plus frappant. L'entreprise n'existe plus, n'a plus d'activité, plus de siège, plus de comptes à publier. Sa fiche, elle, demeure : sur les portails de consultation, chez les rediffuseurs, dans les résultats de recherche. Et avec elle, le nom de ses anciens dirigeants.",
      },
      {
        type: "p",
        text:
          "Cette persistance a des justifications réelles : la vie des affaires a besoin de traçabilité, un créancier ou un juge peut avoir à reconstituer un historique. Mais entre la traçabilité organisée, accessible à ceux qui en ont un motif, et l'exposition nominative permanente au premier venu, il existe tout un éventail de niveaux d'accès. Le débat porte sur le niveau adéquat, pas sur l'existence de la trace.",
      },
      {
        type: "h2",
        text: "Effacer l'histoire n'est pas le sujet",
      },
      {
        type: "p",
        text:
          "Il faut le dire nettement, car c'est l'objection la plus fréquente : demander qu'une ancienne donnée cesse d'occuper une place centrale dans une recherche nominative ne revient pas à demander de réécrire l'histoire. Une donnée peut rester conservée dans sa source juridique, disponible pour ceux qui ont une raison de la consulter, tout en étant moins facilement exposée au public général.",
      },
      {
        type: "p",
        text:
          "C'est exactement la logique du déréférencement : la page d'origine subsiste, mais cesse, si la demande est acceptée, d'être présentée en réponse à certaines recherches portant sur le nom de la personne. D'autres leviers relèvent de la même logique de gradation : masquage d'une fiche chez un rediffuseur, limitation de la diffusion d'une adresse, retrait d'un document téléchargeable. Aucun n'est automatique ; l'ancienneté et la perte de pertinence actuelle comptent parmi les critères pris en considération, aux côtés de la nature de l'information et de l'intérêt du public à y accéder.",
      },
      {
        type: "encadre",
        title: "Cas particulier : les résultats de recherche associés à votre nom",
        text:
          "Lorsque l'exposition tient principalement à un résultat qui apparaît lors d'une recherche sur votre nom, une demande de déréférencement peut être envisagée. La page reste accessible sur son site d'origine, mais cesse, si la demande est acceptée, d'être présentée en réponse à certaines recherches nominatives. Chaque demande est appréciée au cas par cas.",
      },
      {
        type: "quote",
        text:
          "Internet sait conserver. Il sait beaucoup moins bien oublier ce qui a cessé d'être pertinent.",
      },
      {
        type: "links",
        title: "Pour aller plus loin",
        items: [
          { to: "/comprendre/kbis-nest-pas-votre-cv", label: "Votre Kbis n'est pas votre CV" },
          { to: "/comprendre/publicite-legale-ou-exposition-permanente", label: "Publicité légale ou exposition permanente ?" },
          { to: "/comprendre/pappers-origami-donnees-publiques", label: "Pappers, Origami et le faux argument du « c'est public »" },
          { to: "/comprendre/effacement-donnees-personnelles", label: "Effacement, masquage, désindexation : que peut-on réellement demander ?" },
        ],
      },
      {
        type: "note",
        text:
          "Aucune démarche évoquée dans ce texte n'est automatiquement applicable : la réponse dépend de la nature de l'information, de sa source, de son ancienneté, de sa pertinence actuelle et de l'intérêt éventuel du public à y accéder. Ce texte présente un cadre général et ne constitue pas une consultation juridique individualisée.",
      },
    ],
  },
];

// Progression éditoriale de la rubrique Comprendre :
// porte d'entrée conceptuelle → circulation des données → limites de la
// publicité et de l'open data → effets concrets → leviers mobilisables.
const ORDER = [
  "donnee-publique-exposition-publique",
  "rediffuseurs-donnees-entreprises",
  "panorama-acteurs-donnees-entreprises-france",
  "publicite-legale-ou-exposition-permanente",
  "open-data-protection-personnes-limite",
  "pourquoi-votre-adresse-personnelle-sur-internet",
  "effacement-donnees-personnelles",
  "supprimer-adresse-annuaire-en-ligne",
  "pourquoi-horsindex-existe",
  "pappers-origami-donnees-publiques",
  "kbis-nest-pas-votre-cv",
  "anciens-mandats-internet",
];

export const RESSOURCES = ORDER.map((slug) =>
  ARTICLES.find((a) => a.slug === slug)
);

export function findRessourceBySlug(slug) {
  return RESSOURCES.find((r) => r.slug === slug);
}

export function getRelatedRessources(slug, limit = 3) {
  return RESSOURCES.filter((r) => r.slug !== slug).slice(0, limit);
}
